/**
 * Subscription API Client
 * Frontend service for subscription management
 */

import { getToken } from './authApiClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// === TYPES ===

export type SubscriptionStatus =
    | 'pending'
    | 'on_trial'
    | 'active'
    | 'paused'
    | 'past_due'
    | 'cancelled'
    | 'expired'
    | 'unpaid';

export interface SubscriptionInfo {
    status: SubscriptionStatus;
    statusFormatted: string | null;
    planName: string;
    billingInterval: 'monthly' | 'yearly';
    currency: string;
    currentPeriodEnd: string | null;
    renewsAt: string | null;
    cancelledAt: string | null;
    endsAt: string | null;
    updatePaymentMethodUrl: string | null;
    customerPortalUrl: string | null;
    initialCreditsGranted: boolean;
    lastRenewalCreditsAt: string | null;
}

export interface SubscriptionResponse {
    hasSubscription: boolean;
    isActive?: boolean;
    subscription: SubscriptionInfo | null;
    message?: string;
}

export interface CheckoutResponse {
    checkoutUrl: string;
    checkoutId: string;
    testMode: boolean;
    message: string;
}

export interface PortalResponse {
    portalUrl: string;
    updatePaymentUrl: string | null;
}

export interface SubscriptionCheckResponse {
    isActive: boolean;
    planName: string | null;
}

// === HELPERS ===

const getAuthToken = (): string | null => {
    return getToken();
};

const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const token = getAuthToken();

    if (!token) {
        throw new Error('Not authenticated. Please login to continue.');
    }

    const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
    };

    try {
        const response = await fetch(url, {
            ...options,
            headers,
        });

        return response;
    } catch (error) {
        console.error('API request failed:', error);
        throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
};

// === API FUNCTIONS ===

/**
 * Get current user's subscription status
 */
export const getSubscription = async (): Promise<SubscriptionResponse> => {
    const response = await authenticatedFetch(`${API_BASE_URL}/api/subscription`);

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to fetch subscription status');
    }

    return await response.json();
};

/**
 * Create checkout session for new subscription
 * Returns URL to redirect user to LemonSqueezy checkout
 */
export const createSubscriptionCheckout = async (): Promise<CheckoutResponse> => {
    const response = await authenticatedFetch(`${API_BASE_URL}/api/subscription/checkout`, {
        method: 'POST',
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create subscription checkout');
    }

    return await response.json();
};

/**
 * Get customer portal URL for subscription management
 */
export const getSubscriptionPortal = async (): Promise<PortalResponse> => {
    const response = await authenticatedFetch(`${API_BASE_URL}/api/subscription/portal`);

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to get subscription portal');
    }

    return await response.json();
};

/**
 * Quick check if user has active subscription (for UI guards)
 */
export const checkSubscriptionActive = async (): Promise<SubscriptionCheckResponse> => {
    const response = await authenticatedFetch(`${API_BASE_URL}/api/subscription/check`);

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to check subscription status');
    }

    return await response.json();
};

/**
 * Helper: Check if subscription is active
 */
export const isSubscriptionActive = (status: SubscriptionStatus): boolean => {
    return ['active', 'on_trial'].includes(status);
};

/**
 * Helper: Format subscription status for display
 */
export const formatSubscriptionStatus = (status: SubscriptionStatus): string => {
    const statusLabels: Record<SubscriptionStatus, string> = {
        pending: 'Pending',
        on_trial: 'Trial',
        active: 'Active',
        paused: 'Paused',
        past_due: 'Past Due',
        cancelled: 'Cancelled',
        expired: 'Expired',
        unpaid: 'Unpaid',
    };
    return statusLabels[status] || status;
};
